import { useCallback, useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { knowledgeApiSlice } from "api/knowledgeApi.slice";
import { transformKnowledgePackage, useMemoisedSelector } from "common/utilities/react-redux";
import { applyKnowledgeDataSearchCriteria } from "domains/knowledgeData/actions";
import { getKnowledgeDataSearchCriteria } from "domains/knowledgeData/selectors";
import { applyLatestSearchCriteria } from "domains/knowledgeFeed/actions";
import { createKnowledgeFeedForTopicSelector, getLatestSearchCriteria } from "domains/knowledgeFeed/selectors";
import { getModalParams } from "domains/modal/selectors";
import type { ModalParams } from "domains/modal/types";
import { applyPublicationSearchCriteria } from "domains/publication/actions";
import { getPublicationSearchCriteria } from "domains/publication/selectors";
import { applyVideoSearchCriteria } from "domains/videos/actions";
import { getVideoSearchCriteria } from "domains/videos/selectors";
import { fetchKnowledgeById } from "entities/knowledge/actions";
import { createKnowledgesSelector } from "entities/knowledge/selectors";
import { KnowledgeStatus } from "entities/knowledge/types";

const DEFAULT_POLLING_TIMEOUT_LIMIT = 15000;

type UseKnowledgeProcessingParams = {
  isEnabled: boolean;
  knowledgeId?: string;
  onKnowledgeProcessingError: () => void;
};

export const useKnowledgeProcessing = ({
  isEnabled,
  knowledgeId,
  onKnowledgeProcessingError,
}: UseKnowledgeProcessingParams) => {
  const dispatch = useDispatch();

  const knowledgedataSearchCriteria = useSelector(getKnowledgeDataSearchCriteria, shallowEqual);
  const latestSearchCriteria = useSelector(getLatestSearchCriteria, shallowEqual);
  const publicationSearchCriteria = useSelector(getPublicationSearchCriteria, shallowEqual);
  const videoSearchCriteria = useSelector(getVideoSearchCriteria, shallowEqual);

  const { topicId } = useSelector(getModalParams) as ModalParams<"AddKnowledge">;

  const [isPollingKnowledgeStatus, setIsPollingKnowledgeStatus] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);

  const knowledgeFeed = useMemoisedSelector(
    createKnowledgeFeedForTopicSelector(topicId),
    createKnowledgesSelector,
    transformKnowledgePackage,
  );

  const { data, error } = knowledgeApiSlice.endpoints.getKnowledgeStatusById.useQuery(
    { id: knowledgeId ?? "" },
    {
      skip: !knowledgeId,
      pollingInterval: isPollingKnowledgeStatus ? 1000 : 0,
    },
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isPollingKnowledgeStatus) return;

      setIsPollingKnowledgeStatus(false);
      setIsTimeout(true);
    }, DEFAULT_POLLING_TIMEOUT_LIMIT);

    return () => clearTimeout(timeout);
  });

  const needsProcessing = useMemo(() => {
    if (knowledgeId && !data) return true;

    if (!data) return false;

    return data.status === KnowledgeStatus.NOT_STARTED || data.status === KnowledgeStatus.PROCESSING;
  }, [knowledgeId, data]);

  const isProcessingComplete = useMemo(() => {
    if (!data) return false;
    // Processing is finished once we have a status that isn't "still going"
    // (Not Started / Processing). Done, Error, and anything unexpected like a
    // missing status all count as finished, so the modal stops waiting.
    return !needsProcessing || !!error;
  }, [data, needsProcessing, error]);

  const refetchTopicSections = useCallback(() => {
    dispatch(
      applyPublicationSearchCriteria(
        { limit: publicationSearchCriteria.limit + publicationSearchCriteria.offset, offset: 0 },
        { forceRefresh: true },
      ),
    );
    dispatch(
      applyVideoSearchCriteria(
        { limit: videoSearchCriteria.limit + videoSearchCriteria.offset, offset: 0 },
        { forceRefresh: true },
      ),
    );
    dispatch(
      applyKnowledgeDataSearchCriteria(
        { limit: knowledgedataSearchCriteria.limit + knowledgedataSearchCriteria.offset, offset: 0 },
        { forceRefresh: true },
      ),
    );

    if (knowledgeFeed.length > 0)
      dispatch(applyLatestSearchCriteria({ ...latestSearchCriteria, limit: knowledgeFeed.length, offset: 0, topicId }));
  }, [
    dispatch,
    knowledgeFeed.length,
    knowledgedataSearchCriteria.limit,
    knowledgedataSearchCriteria.offset,
    latestSearchCriteria,
    publicationSearchCriteria.limit,
    publicationSearchCriteria.offset,
    topicId,
    videoSearchCriteria.limit,
    videoSearchCriteria.offset,
  ]);

  // Determine if we should be polling
  const shouldBePolling = useMemo(
    () => isEnabled && !!knowledgeId && needsProcessing && !isProcessingComplete,
    [isEnabled, knowledgeId, needsProcessing, isProcessingComplete],
  );

  useEffect(() => {
    if (shouldBePolling) {
      setIsTimeout(false);
      setIsPollingKnowledgeStatus(true);
    } else {
      setIsPollingKnowledgeStatus(false);
    }
  }, [shouldBePolling]);

  useEffect(() => {
    if (!data) return;

    if (data.status === KnowledgeStatus.DONE) {
      refetchTopicSections();
      dispatch(fetchKnowledgeById({ id: knowledgeId ?? "" }));
    }

    if (data.status === KnowledgeStatus.ERROR) onKnowledgeProcessingError();
  }, [dispatch, data, refetchTopicSections, knowledgeId, onKnowledgeProcessingError]);

  return { isTimeout, isProcessingComplete, hasError: !!error };
};
